/**
 * @fileoverview New monitor creation page component.
 * Provides a form for creating new API monitors with configuration options
 * for URL, method, interval, headers, body, and alert conditions.
 * Redirects to dashboard on successful creation.
 * @module pages/new-monitor
 */

import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth.js";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/authUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, ChevronLeft } from "lucide-react";
import { Link, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { insertMonitorSchema, type InsertMonitor } from "@shared/schema";
import { z } from "zod";
import { MonitorForm } from "@/components/MonitorForm";

/** Extended form schema with headers as string for JSON input */
const formSchema = insertMonitorSchema.extend({
  headers: z.string().optional(),
});

/** Form data type inferred from formSchema */
type FormData = z.infer<typeof formSchema>;

/**
 * New monitor page component.
 * Protected page that displays a form for creating new monitors.
 * Handles form submission, JSON header parsing, and API creation.
 * @returns New monitor page JSX
 */
export default function NewMonitor() {
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  const createMonitorMutation = useMutation({
    mutationFn: async (data: FormData) => {
      let headers = undefined;
      if (data.headers && data.headers.trim()) {
        try {
          headers = JSON.parse(data.headers);
        } catch {
          throw new Error("Invalid JSON in headers");
        }
      }

      const monitorData: InsertMonitor = {
        name: data.name,
        url: data.url,
        method: data.method,
        interval: data.interval,
        headers,
        body: data.body || undefined,
        alertStatusCodes: data.alertStatusCodes,
        isActive: data.isActive,
      };

      await apiRequest("POST", "/api/monitors", monitorData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitors"] });
      toast({
        title: "Success",
        description: "Monitor created successfully",
      });
      setLocation("/");
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: error.message || "Failed to create monitor",
        variant: "destructive",
      });
    },
  });

  /**
   * Handles form submission.
   * @param data - Form data from MonitorForm
   */
  const handleSubmit = (data: FormData) => {
    createMonitorMutation.mutate(data);
  };

  /**
   * Handles cancel button click.
   * Navigates back to dashboard.
   */
  const handleCancel = () => {
    setLocation("/");
  };

  if (authLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="new-monitor-page">
      <header className="border-b sticky top-0 bg-background z-50" data-testid="new-monitor-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-testid="new-monitor-navbar-container">
          <Link href="/" className="flex items-center gap-2" data-testid="new-monitor-navbar-home-link">
            <Activity className="h-6 w-6 text-primary" data-testid="new-monitor-navbar-logo-icon" />
            <span className="text-lg font-semibold" data-testid="new-monitor-navbar-brand-text">Clockwork</span>
          </Link>
          <a href="/api/logout" data-testid="new-monitor-navbar-logout-link">
            <Button variant="ghost" data-testid="new-monitor-navbar-logout-button">Log Out</Button>
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="new-monitor-main-content">
        <div className="mb-6" data-testid="new-monitor-header">
          <Link href="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="new-monitor-back-link">
            <ChevronLeft className="h-4 w-4 mr-1" data-testid="new-monitor-back-icon" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="new-monitor-title">Create New Monitor</h1>
          <p className="text-muted-foreground mt-2" data-testid="new-monitor-description">
            Configure a new API endpoint to monitor. We'll check it regularly and alert you if it goes down.
          </p>
        </div>

        <Card data-testid="new-monitor-form-card">
          <CardHeader data-testid="new-monitor-form-header">
            <CardTitle data-testid="new-monitor-form-title">Monitor Configuration</CardTitle>
          </CardHeader>
          <CardContent data-testid="new-monitor-form-content">
            <MonitorForm
              onSubmit={handleSubmit}
              submitLabel="Start Monitoring"
              isPending={createMonitorMutation.isPending}
              onCancel={handleCancel}
            // Pass a base testid for the form to inherit
            // Note: Relying on MonitorForm's internal unique IDs for now
            />
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
